import React from 'react';
import PropTypes from 'prop-types';

import { appPropTypes, useLanguageContext } from '../../../../_shared';

import { CONTINUATION_TYPES } from '../../../constants';
import { formFieldShape } from '../../../utils';
import FormField from '../../FormField';
import FormSection from '../../FormSection';

const propTypes = {

	/**
	 * The index of the section relative to the form.
	 */
	index: PropTypes.number.isRequired,

	/**
	 * The form section (with the fields already ordered based on weight).
	 */
	formSection: PropTypes.shape({

		/**
		 * The form section ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The title (DEPRECATED, has been moved to form_section_contents).
		 */
		title: PropTypes.string,

		/**
		 * The description (DEPRECATED, has been moved to form_section_contents).
		 */
		description: PropTypes.string,

		/**
		 * The type of navigation to apply to the section.
		 */
		continuation_type: PropTypes.oneOf([
			CONTINUATION_TYPES.SUBMIT,
			CONTINUATION_TYPES.DEFAULT,
			CONTINUATION_TYPES.SECTION,
			CONTINUATION_TYPES.PRODUCT_TICKETS,
			CONTINUATION_TYPES.PRODUCT_TICKET_GROUPS,
		]),

		/**
		 * The ID of the section to navigate to after this section, if any.
		 */
		continuation_section_id: PropTypes.string,

		/**
		 * The list of form fields in the section.
		 */
		fields: PropTypes.arrayOf(formFieldShape).isRequired,

		/**
		 * The multi-language content for the form section.
		 */
		form_section_contents: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The language.
			 */
			language: PropTypes.string.isRequired,

			/**
			 * The title.
			 */
			title: PropTypes.string,

			/**
			 * The description.
			 */
			description: PropTypes.string,

		})).isRequired,

	}).isRequired,

	/**
	 * The current form data.
	 */
	data: PropTypes.objectOf(PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
		PropTypes.bool,
		PropTypes.object,
		PropTypes.arrayOf(PropTypes.string),
	])).isRequired,

	/**
	 * The set of validation errors for form fields.
	 */
	errors: PropTypes.objectOf(PropTypes.bool).isRequired,

	/**
	 * True, if the user is the editing the the form.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the user is the previewing the the form.
	 */
	isPreview: PropTypes.bool,

	/**
	 * The settings for filestack.
	 */
	filestack: appPropTypes.filestack,

	/**
	 * Callback to handle changes to form field inputs.
	 */
	onChange: PropTypes.func,

};

/**
 * Renders a section in the embedded form.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const EmbeddedFormSection = (props) => {

	const {
		index,
		formSection,
		data,
		errors,
		isEditing,
		isPreview,
		filestack,
		onChange,
	} = props;

	const { chooseContentFromLanguage } = useLanguageContext();

	if (!formSection) {
		return null;
	}

	// Get the translated content for the current form section, if any
	const currentFormSectionContent = chooseContentFromLanguage(
		formSection.form_section_contents
	);

	return (
		<FormSection
			section={{
				...formSection,
				title: currentFormSectionContent?.title || formSection.title,
				description: currentFormSectionContent?.description || formSection.description,
			}}
			index={index}>
			{formSection.fields?.map((field) => {

				// Get the translated content for the form field
				const formFieldContent = chooseContentFromLanguage(field.form_field_contents);

				// Get the translated content for the form field options
				const options = field.form_field_options?.map((option) => {

					// Get the translated content for the form field option
					const formFieldOptionContent = chooseContentFromLanguage(
						option.form_field_option_contents
					);

					return {
						...option,
						label: formFieldOptionContent?.label || option.label,
					};

				});

				return (
					<FormField
						key={field.id}
						type={field.type}
						name={field.id}
						value={data[field.id]}
						options={options}
						label={formFieldContent?.title || field.title}
						placeholder={
							formFieldContent?.placeholder ||
							formFieldContent?.title ||
							field.placeholder ||
							field.title
						}
						help={formFieldContent?.help || field.help}
						error={errors[`${field.id}_msg`]}
						isEditing={isEditing}
						isInvalid={errors[field.id]}
						isPreview={isPreview}
						isRequired={field.required}
						filestack={filestack}
						isHelpRichText
						onChange={onChange} />
				);

			})}
		</FormSection>
	);

};

EmbeddedFormSection.propTypes = propTypes;

export default EmbeddedFormSection;
